import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@/lib/contexts/UserContext";
import { useLocation } from 'react-router-dom';
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from '@/lib/auth/useAuth';
import type { User } from '@/components/settings/types';
import { createOrganization } from '@/api/organizations';

interface OnboardingStep {
  title: string;
  description: string;
  fields: {
    key: string;
    label: string;
    type: 'text' | 'email' | 'select';
    required: boolean;
    options?: string[];
  }[];
}

// Base steps that are always shown
const baseOnboardingSteps: OnboardingStep[] = [
  {
    title: "Welcome to EvalOps!",
    description: "Let's get to know you better to personalize your experience.",
    fields: [
      {
        key: "firstName",
        label: "First Name",
        type: "text",
        required: true
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "text",
        required: false
      }
    ]
  },
  {
    title: "Your Role",
    description: "Help us understand your role to provide relevant features.",
    fields: [
      {
        key: "role",
        label: "What best describes your role?",
        type: "select",
        required: true,
        options: ["Developer", "DevOps Engineer", "SRE", "QA Engineer", "Product Manager", "Other"]
      }
    ]
  },
  {
    title: "Your Use Case",
    description: "Tell us about your evaluation needs to help us provide the best experience.",
    fields: [
      {
        key: "useCase",
        label: "What are you primarily looking to evaluate?",
        type: "select",
        required: true,
        options: [
          "LLM Response Quality",
          "Model Performance & Latency",
          "Safety & Content Filtering",
          "Hallucination Detection",
          "Multi-Model Comparison",
          "Custom Metrics & Assertions",
          "Other"
        ]
      },
      {
        key: "teamSize",
        label: "How many people will be using EvalOps?",
        type: "select",
        required: true,
        options: ["Just me", "2-5 people", "6-10 people", "11-25 people", "26+ people"]
      },
      {
        key: "evaluationVolume",
        label: "Estimated evaluations per month?",
        type: "select",
        required: true,
        options: [
          "Under 500",
          "500 - 2,000",
          "2,000 - 10,000",
          "10,000 - 25,000",
          "Over 25,000"
        ]
      }
    ]
  },
  {
    title: "Your Tech Stack",
    description: "Help us integrate seamlessly with your existing tools.",
    fields: [
      {
        key: "ciTool",
        label: "Which CI/CD platform do you use?",
        type: "select",
        required: true,
        options: [
          "GitHub Actions",
          "GitLab CI",
          "CircleCI",
          "Jenkins",
          "Azure DevOps",
          "Other",
          "None / Not Sure"
        ]
      },
      {
        key: "llmProvider",
        label: "Which LLM provider(s) do you use?",
        type: "select",
        required: true,
        options: [
          "OpenAI (GPT-4, GPT-3.5)",
          "Anthropic (Claude)",
          "Google (Gemini/PaLM)",
          "Cohere",
          "Hugging Face",
          "Self-hosted / Open Source",
          "Multiple Providers",
          "Other"
        ]
      }
    ]
  },
  {
    title: "Preferences",
    description: "Set up your notification preferences.",
    fields: [
      {
        key: "notifications",
        label: "How would you like to receive notifications?",
        type: "select",
        required: true,
        options: ["Email", "Slack", "Both", "None"]
      }
    ]
  }
];

// Organization setup step (only shown if user has no organizations)
const organizationSetupStep: OnboardingStep = {
  title: "Set Up Your Organization",
  description: "Create your organization to manage your evaluations and team.",
  fields: [
    {
      key: "orgName",
      label: "Organization Name",
      type: "text",
      required: true
    },
    {
      key: "orgIndustry",
      label: "Industry",
      type: "select",
      required: true,
      options: [
        "Technology / Software",
        "Financial Services",
        "Healthcare",
        "E-commerce / Retail",
        "Education",
        "Media / Entertainment",
        "Government",
        "Non-profit",
        "Other"
      ]
    },
    {
      key: "orgSize",
      label: "Company Size",
      type: "select",
      required: true,
      options: [
        "1-10 employees",
        "11-50 employees",
        "51-200 employees",
        "201-1000 employees",
        "1000+ employees"
      ]
    },
    {
      key: "orgWebsite",
      label: "Organization Website",
      type: "text",
      required: false
    },
    {
      key: "orgEmailDomains",
      label: "Allowed Email Domains (comma-separated)",
      type: "text",
      required: false
    }
  ]
};

interface OnboardingModalProps {
  forceOpen?: boolean;
}

export function OnboardingModal({ forceOpen }: OnboardingModalProps) {
  const { user, updateUser, isLoading } = useUser();
  const { user: authUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const { toast } = useToast();
  const location = useLocation();
  const isOnboardingPage = location.pathname === '/onboarding';
  
  // Determine if user has organizations
  const userHasOrganizations = useMemo(() => {
    // Check if user has organizations from auth data
    return authUser?.organizations && authUser.organizations.length > 0;
  }, [authUser]);
  
  // Build dynamic onboarding steps based on user's organization status
  const onboardingSteps = useMemo(() => {
    const steps = [...baseOnboardingSteps];
    
    // Only add organization setup if user doesn't have any organizations
    if (!userHasOrganizations) {
      // Insert organization step after role selection (index 2)
      steps.splice(2, 0, organizationSetupStep);
    }
    
    return steps;
  }, [userHasOrganizations]);

  // Don't render if still loading or no user
  if (isLoading || !user) {
    return null;
  }

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = async () => {
    // Check if user state is ready
    if (isLoading || !user?.id) {
      toast({
        title: "Please wait",
        description: "We're still loading your profile. Please try again in a moment.",
        variant: "destructive",
      });
      return;
    }

    const currentFields = onboardingSteps[currentStep].fields;
    const requiredFields = currentFields.filter(field => field.required);
    
    // Validate required fields
    const missingFields = requiredFields.filter(field => !formData[field.key]);
    if (missingFields.length > 0) {
      toast({
        title: "Missing Information",
        description: `Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`,
        variant: "destructive",
      });
      return;
    }

    // For organization step, just advance to the next step
    // Organization will be created server-side when we submit the final form
    if (currentStep === 2) {
      setCurrentStep(prev => prev + 1);
      return;
    }

    if (currentStep === onboardingSteps.length - 1) {
      // Last step - submit all data
      setIsSubmitting(true);
      try {
        // First, create organization if user doesn't have one
        if (!userHasOrganizations && formData.orgName) {
          try {
            const orgResult = await createOrganization({
              name: formData.orgName,
              metadata: {
                description: `${formData.orgIndustry} company with ${formData.orgSize}`,
                website: formData.orgWebsite || '',
                allowedEmailDomains: formData.orgEmailDomains || '',
                features: JSON.stringify({
                  industry: formData.orgIndustry,
                  companySize: formData.orgSize
                })
              }
            });
            
            if (!orgResult.success) {
              throw new Error(orgResult.error || 'Failed to create organization');
            }
            
            // Organization created successfully
            if (import.meta.env.DEV) {
              console.log('[Onboarding] Organization created:', orgResult.data);
            }
          } catch (orgError) {
            console.error('[Onboarding] Error creating organization:', orgError);
            toast({
              title: "Organization Creation Failed",
              description: "We couldn't create your organization. You can create one later from settings.",
              variant: "destructive",
            });
            // Continue with user profile update even if org creation fails
          }
        }
        
        // Update user profile with all collected data
        const updateData: Partial<User> & { notification_preference?: string } = {
          name: `${formData.firstName} ${formData.lastName || ''}`.trim(),
          email: user.email,
          role: 'member' as const,
          notification_preference: formData.notifications,
          onboarding_completed: true,
          metadata: {
            ...user.metadata,
            firstName: formData.firstName,
            lastName: formData.lastName || '',
            jobRole: formData.role,
            useCase: formData.useCase,
            teamSize: formData.teamSize,
            evaluationVolume: formData.evaluationVolume,
            ciTool: formData.ciTool,
            llmProvider: formData.llmProvider,
            onboardingCompletedAt: new Date().toISOString()
          }
        };
        
        await updateUser(updateData);
        
        // Remove localStorage backup approach which might interfere with state
        if (localStorage.getItem('onboarding_completed')) {
          localStorage.removeItem('onboarding_completed');
        }
        
        toast({
          title: "Welcome to EvalOps!",
          description: "Your profile has been set up successfully.",
        });
        
        // Close the modal after successful completion
        setIsOpen(false);
        
        // If we created an organization, we might need to refresh the page
        // to ensure the new organization is loaded properly
        if (!userHasOrganizations && formData.orgName) {
          window.location.reload();
        }
      } catch (error) {
        console.error('[Onboarding] Error updating user:', error);
        toast({
          title: "Error",
          description: "Failed to save your information. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const currentStepData = onboardingSteps[currentStep];

  // If we're on the onboarding page, render the content directly
  if (isOnboardingPage) {
    return (
      <div className="max-w-[425px] mx-auto p-6">
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-bold">{currentStepData.title}</h1>
            <p className="text-sm text-muted-foreground">{currentStepData.description}</p>
          </div>
          
          <div className="space-y-4">
            {currentStepData.fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key}>{field.label}</Label>
                {field.type === 'select' ? (
                  <select
                    id={field.key}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    value={formData[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                  >
                    <option value="">Select an option</option>
                    {field.options?.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id={field.key}
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    required={field.required}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-between pt-4">
            {currentStep > 0 && (
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button 
              className="ml-auto" 
              onClick={handleNext}
              disabled={isSubmitting || isLoading}
            >
              {currentStep === onboardingSteps.length - 1 ? 'Complete' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{currentStepData.title}</DialogTitle>
          <DialogDescription>
            {currentStepData.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {currentStepData.fields.map((field) => (
            <div key={field.key} className="space-y-2">
              <Label htmlFor={field.key}>{field.label}</Label>
              {field.type === 'select' ? (
                <select
                  id={field.key}
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                >
                  <option value="">Select an option</option>
                  {field.options?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id={field.key}
                  type={field.type}
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  required={field.required}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-between pt-4">
          {currentStep > 0 && (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button 
            className="ml-auto" 
            onClick={handleNext}
            disabled={isSubmitting || isLoading}
          >
            {currentStep === onboardingSteps.length - 1 ? 'Complete' : 'Next'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 